import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/axios';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('vendorToken');
    if (token) {
      checkAuthStatus();
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await api.get('/auth/vendor/me');
      const vendorData = response.data;
      setUser(vendorData); // For vendor login, user and vendor are the same
      setVendor(vendorData);
    } catch (error) {
      localStorage.removeItem('vendorToken');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/vendor/login', {
        email,
        password,
      });
      const { token, vendor: vendorData } = response.data;

      localStorage.setItem('vendorToken', token);

      // Call checkAuthStatus to ensure proper authentication state
      await checkAuthStatus();

      showSuccessToast('Login successful!', 'login');
      return true;
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      showErrorToast(message, 'login');
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('vendorToken');
    setUser(null);
    setVendor(null);
    showSuccessToast('Logged out successfully', 'logout');
  };

  // Add this function to update vendor data
  const updateVendorData = (updatedVendorData) => {
    setUser(updatedVendorData);
    setVendor(updatedVendorData);
  };

  const value = {
    user,
    vendor,
    isAuthenticated: !!user,
    loading,
    login,
    logout,
    updateVendorData, // Add this to the context value
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
